
import { Booth } from '../types';
import { MAP_CONFIG, GOOGLE_SHEETS_CSV_URL } from '../constants';

/**
 * 健壯的 CSV 解析器：支援引號包裹的欄位（處理 HTML 中可能出現的逗號、換行）
 */
const parseCSV = (csvText: string): Booth[] => {
  const rows: string[][] = [];
  let currentField = '';
  let inQuotes = false;
  let currentRow: string[] = [];

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        currentField += '"';
        i++; // 跳過下一個引號
      } else if (char === '"') {
        inQuotes = false;
      } else {
        currentField += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        currentRow.push(currentField.trim());
        currentField = '';
      } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
        currentRow.push(currentField.trim());
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
        if (char === '\r') i++; // 跳過 \n
      } else {
        currentField += char;
      }
    }
  }
  
  // 處理最後一行
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    rows.push(currentRow);
  }

  if (rows.length < 2) return [];

  const headers = rows[0].map(h => h.trim());
  // 建立輔助函式：尋找欄位索引（支援英文、中文以及多種別名）
  const findIndex = (enName: string, aliases: string[]) => {
    const enIdx = headers.indexOf(enName);
    if (enIdx !== -1) return enIdx;
    
    for (const alias of aliases) {
      const idx = headers.indexOf(alias);
      if (idx !== -1) return idx;
    }
    return -1;
  };

  const idx = {
    id: findIndex('id', ['攤位號', '編號', '攤位編號', '位置', 'Booth ID']),
    circleName: findIndex('circleName', ['社團名', '名稱', '社團名稱', 'Circle']),
    author: findIndex('author', ['攤主', '作者', 'Circle Author', 'Owner']),
    bannerUrl: findIndex('bannerUrl', ['Banner圖', '封面圖', 'Banner', 'Image', '宣傳圖']),
    avatarUrl: findIndex('avatarUrl', ['正圓形頭像', '頭像', 'Avatar', 'Logo']),
    socialUrl: findIndex('socialUrl', ['商品', '社群網址', '連結', 'SNS', '社群', '網站', '網址', '個人網站', '個人首頁', 'Link', 'SNS連結']),
    keywords: findIndex('keywords', ['關鍵字', '作品關鍵字', 'Keywords']),
    tags: findIndex('tags', ['標籤', '類型', 'Genre', '分類']),
    description: findIndex('description', ['宣傳文', '介紹', 'Description', 'Info', '宣傳內容'])
  };

  const results: Booth[] = [];

  for (let i = 1; i < rows.length; i++) {
    const values = rows[i];
    if (values.length < 2) continue;

    const id = (idx.id !== -1 ? values[idx.id] : '') || `Unknown-${i}`;
    const row = id.charAt(0).toUpperCase();
    const indexStr = id.substring(1);
    const index = parseInt(indexStr, 10) || 0;

    const author = (idx.author !== -1 ? values[idx.author] : '') || '未知攤主';
    const bannerUrl = (idx.bannerUrl !== -1 ? values[idx.bannerUrl] : '') || '';
    const avatarUrl = (idx.avatarUrl !== -1 ? values[idx.avatarUrl] : '') || '';
    const socialUrl = (idx.socialUrl !== -1 ? values[idx.socialUrl] : '') || '#';
    const keywords = (idx.keywords !== -1 && values[idx.keywords]) ? values[idx.keywords].split(/[;，,]/).map(k => k.trim()) : [];
    const tags = (idx.tags !== -1 && values[idx.tags]) ? values[idx.tags].split(/[;，,]/).map(t => t.trim()) : [];
    const description = (idx.description !== -1 ? values[idx.description] : '') || '尚無宣傳資料內容。';

    // 計算填寫完整度 (修正：只計算 網址、關鍵字、宣傳文)
    let filledCount = 0;
    if (socialUrl && socialUrl !== '#' && socialUrl !== 'https://twitter.com' && socialUrl.trim() !== '') filledCount++;
    if (keywords.length > 0 && keywords[0] !== '') filledCount++;
    if (description && !description.includes('尚無宣傳資料內容') && description.trim() !== '') filledCount++;
    
    const completeness = filledCount / 3;

    results.push({
      id,
      row,
      index,
      circleName: (idx.circleName !== -1 ? values[idx.circleName] : '') || '未命名社團',
      author,
      bannerUrl,
      avatarUrl,
      socialUrl,
      keywords,
      tags,
      description,
      completeness
    });
  }

  return results;
};

/**
 * 獲取模擬資料
 */
const getSimulatedData = (): Booth[] => {
  const booths: Booth[] = [];
  MAP_CONFIG.columns.forEach(col => {
    const count = MAP_CONFIG.columnCounts[col];
    for (let i = 1; i <= count; i++) {
      const id = `${col}${i.toString().padStart(2, '0')}`;
      
      const author = `作者 ${id}`;
      const avatarUrl = `https://picsum.photos/seed/${id}avatar/200/200`;
      const socialUrl = 'https://twitter.com';
      const keywords = ['同人', '創作', col];
      const description = `<p>這是 <strong>${id}</strong> 攤位的 HTML 介紹。</p><ul style="list-style-type: disc; padding-left: 20px;"><li>項目一</li><li>項目二</li></ul><p style="color: orange;">支援樣式設定！</p>`;

      // 模擬資料的完整度計算 (模擬某些已填寫)
      let filledCount = 0;
      const sUrl = socialUrl as string;
      if (sUrl && sUrl !== '#' && sUrl !== 'https://twitter.com') filledCount++;
      if (keywords.length > 0) filledCount++;
      if (description && description !== '尚無宣傳資料內容。') filledCount++;
      const completeness = filledCount / 3;

      booths.push({
        id,
        row: col,
        index: i,
        circleName: `${col}排的社團 ${i}`,
        author,
        bannerUrl: `https://picsum.photos/seed/${id}banner/768/472`,
        avatarUrl,
        socialUrl,
        keywords,
        tags: [i % 2 === 0 ? '新品' : '既刊', i % 3 === 0 ? '18+' : '全年齡'],
        description,
        completeness
      });
    }
  });
  return booths;
};

export const fetchBoothData = async (): Promise<Booth[]> => {
  if (!GOOGLE_SHEETS_CSV_URL) {
    await new Promise(resolve => setTimeout(resolve, 800));
    return getSimulatedData();
  }

  try {
    const response = await fetch(GOOGLE_SHEETS_CSV_URL);
    if (!response.ok) throw new Error('無法讀取試算表資料');
    const csvText = await response.text();
    return parseCSV(csvText);
  } catch (error) {
    console.error('Fetch error:', error);
    return getSimulatedData();
  }
};
